import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useLiftLmsBlockMutation,
  useLmsBlockRobotLatestRunQuery,
  useLmsSuspensionsPageQuery,
  useStudentLmsBlockHistoryQuery,
} from 'core/graphql';
import {
  buildLmsBlockVariables,
  ILmsAmountDue,
  ILmsBlockInvoice,
  ILmsBlockNotification,
  LmsSuspensionStatus,
  offsetAfterRemoval,
} from 'core/utils/lmsSuspension';

import { useToasts } from '../Toast/useToasts';
import { lmsBlockErrorText } from './lmsBlockErrors';
import type { ILmsBlockActionOptions } from './useLmsAccessBlock';
import { useStudentPermissions } from './useStudentPermissions';

export const LMS_SUSPENSIONS_PAGE_SIZE = 50;

export interface ILmsSuspensionRow {
  status: string;
  studentId: string;
  studentName: string;
  studentEmail?: string | null;
  groups: Array<{ id: string; name: string }>;
  source?: string | null;
  reason?: string | null;
  blockedAt?: string | null;
  blockedByName?: string | null;
  restoredAt?: string | null;
  restoredByName?: string | null;
  restoredSource?: string | null;
  skipReason?: string | null;
  invoices: ILmsBlockInvoice[];
  amountsDue: ILmsAmountDue[];
  notifications: ILmsBlockNotification[];
}

export interface ILmsBlockRobotRun {
  id: string;
  status: string;
  triggerSource?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  stats?: unknown;
  errorMessage?: string | null;
}

export interface IUseLmsSuspensions {
  rows: ILmsSuspensionRow[];
  total: number;
  offset: number;
  limit: number;
  setOffset: (offset: number) => void;
  /** First load only — later reloads keep the previous page on screen. */
  initialLoading: boolean;
  fetching: boolean;
  error?: Error;
  robotRun: ILmsBlockRobotRun | null;
  robotLoading: boolean;
  restoring: boolean;
  /** Restore one student from the «active» list; re-reads the SAME page. */
  restore: (
    studentId: string,
    options?: ILmsBlockActionOptions,
  ) => Promise<boolean>;
}

/**
 * ТЗ 0074 «Wstrzymany dostęp» tab: one server page of suspensions (offset /
 * limit, not accumulated), the robot's last run, and the restore action.
 *
 * Nothing is requested for roles outside PAYMENT_ACCESS_ROLES — a route the
 * role can open is not a query the backend lets it run.
 *
 * After a restore the page is re-read with its OWN variables via `refetch`
 * (never by operation name: a skipped observable of the same name would be
 * fired with empty variables). If that page would become empty, the view
 * steps back one page instead.
 */
export const useLmsSuspensions = (
  status: LmsSuspensionStatus,
  search: string,
): IUseLmsSuspensions => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const { canBlockLmsAccess } = useStudentPermissions();
  const limit = LMS_SUSPENSIONS_PAGE_SIZE;

  // The offset belongs to the filter it was chosen under: a new status or
  // search starts from the first page in the SAME render (an effect-based
  // reset would first fire one request with the stale offset).
  const filterKey = `${status}|${search}`;
  const [paging, setPaging] = useState({ key: filterKey, offset: 0 });
  const offset = paging.key === filterKey ? paging.offset : 0;
  const setOffset = useCallback(
    (next: number) => setPaging({ key: filterKey, offset: next }),
    [filterKey],
  );

  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useLmsSuspensionsPageQuery({
    variables: {
      status,
      search: search || null,
      offset,
      limit,
    },
    skip: !canBlockLmsAccess,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const robot = useLmsBlockRobotLatestRunQuery({
    skip: !canBlockLmsAccess,
    fetchPolicy: 'cache-and-network',
  });

  const [liftMut, liftState] = useLiftLmsBlockMutation();

  const page = (data ?? previousData)?.lmsSuspensions;
  const total = page?.total ?? 0;

  const restore = useCallback(
    async (studentId: string, options?: ILmsBlockActionOptions) => {
      try {
        const res = await liftMut({
          variables: buildLmsBlockVariables(studentId, options),
        });
        const payload = res.data?.unblockStudentLmsAccess;
        if (!payload?.success) {
          addToast({
            data: {
              type: ToastTypes.error,
              title: t('pages.studentDetail.lmsBlock.failed'),
              text: lmsBlockErrorText(payload?.customErrors, t),
            },
            type: GlobalToastTypeNames.LmsAccessBlock,
          });
          return false;
        }
        addToast({
          data: {
            type: ToastTypes.success,
            title: t('pages.studentDetail.lmsBlock.unblocked'),
          },
          type: GlobalToastTypeNames.LmsAccessBlock,
        });
        const next = offsetAfterRemoval(offset, limit, total);
        if (next !== offset) setOffset(next);
        else await refetch();
        return true;
      } catch (e) {
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('pages.studentDetail.lmsBlock.failed'),
            text: (e as Error).message,
          },
          type: GlobalToastTypeNames.LmsAccessBlock,
        });
        return false;
      }
    },
    [liftMut, addToast, t, offset, limit, total, refetch, setOffset],
  );

  const rows = useMemo<ILmsSuspensionRow[]>(() => page?.items ?? [], [page]);

  return {
    rows,
    total,
    offset,
    limit,
    setOffset,
    initialLoading: loading && !page,
    fetching: loading,
    error,
    robotRun: robot.data?.lmsBlockRobotLastRun ?? null,
    robotLoading: robot.loading && !robot.data,
    restoring: liftState.loading,
    restore,
  };
};

export interface ILmsBlockEvent {
  id: string;
  action: string;
  source: string;
  actorName?: string | null;
  createdAt: string;
  reason?: string | null;
  notifyStudent: boolean;
  invoices: ILmsBlockInvoice[];
  amountsDue: ILmsAmountDue[];
  notifications: ILmsBlockNotification[];
}

const EMPTY_EVENTS: ILmsBlockEvent[] = [];

/**
 * ТЗ 0074: a student's suspension history for the block dialog. Skipped for
 * roles without the right and whenever `enabled` is false.
 */
export const useLmsBlockHistory = (
  studentId: string | undefined,
  enabled = true,
): { events: ILmsBlockEvent[]; loading: boolean; error?: Error } => {
  const { canBlockLmsAccess } = useStudentPermissions();
  const { data, loading, error } = useStudentLmsBlockHistoryQuery({
    variables: { studentId: studentId || '' },
    skip: !studentId || !enabled || !canBlockLmsAccess,
    fetchPolicy: 'cache-and-network',
  });
  return {
    events: data?.studentAccessBlockEvents ?? EMPTY_EVENTS,
    loading: loading && !data,
    error,
  };
};
